import React, { FC, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import { BUSINESS_SETTING_LABELS } from 'hooks/business/useBusinessSettings';
import { getAssistMigrationColumnVisibilityModel } from 'utils/assist/getAssistMigrationColumnVisibilityModel';
import { toAssistMigrationAppliedFilters } from 'utils/assist/toAssistMigrationAppliedFilters';
import { useAssistMigrationProfiles } from 'api/queries/assist/useAssistMigrationProfiles';
import {
  ASSIST_MIGRATION_ASSIST_ENABLED_FILTER_FIELD,
  ASSIST_MIGRATION_DEFAULT_BUSINESS_SETTINGS,
  ASSIST_MIGRATION_DEFAULT_FILTERS,
  ASSIST_MIGRATION_DEFAULT_FLAG_KEYS,
  ASSIST_MIGRATION_DEFAULT_HIDDEN_COLUMNS,
  ASSIST_MIGRATION_SELF_SERVE_FILTER_FIELD,
  ASSIST_VERSION_LABELS,
  AssistVersion,
} from 'constants/assist/assistMigration';
import { AssistMigrationProfile } from 'types/assist/assistMigration';
import { AssistMigrationModal } from './AssistMigrationModal';
import './AssistMigrationTable.scss';
import { AssistMigrationTableToolbar } from './AssistMigrationTableToolbar';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const HIDDEN_BUSINESS_SETTINGS_KEYS = ['id', 'businessId'];

const VERSION_CHIP_COLOR: Record<AssistVersion, 'default' | 'warning' | 'success'> = {
  [AssistVersion.NOT_ENABLED]: 'default',
  [AssistVersion.LEGACY]: 'warning',
  [AssistVersion.SELF_SERVE]: 'success',
};

const renderBoolean = (value: boolean) => (value ? '✓' : '—');

const renderSettingValue = (value: unknown) => {
  if (typeof value === 'boolean') {
    return renderBoolean(value);
  }
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  return String(value);
};

const collectKeys = (
  profiles: AssistMigrationProfile[],
  pick: (profile: AssistMigrationProfile) => Record<string, unknown> | undefined,
  exclude: string[] = [],
): string[] => {
  const keys = new Set<string>();
  profiles.forEach((profile) => {
    Object.keys(pick(profile) ?? {}).forEach((key) => {
      if (!exclude.includes(key)) {
        keys.add(key);
      }
    });
  });

  return Array.from(keys).sort();
};

interface ModalState {
  open: boolean;
  businessId: number | null;
  businessName: string;
}

const CLOSED_MODAL: ModalState = { open: false, businessId: null, businessName: '' };

export const AssistMigrationTable: FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [primaryFilters, setPrimaryFilters] = useState<Record<string, string>>({
    ...ASSIST_MIGRATION_DEFAULT_FILTERS,
  });
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});
  const [modal, setModal] = useState<ModalState>(CLOSED_MODAL);
  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>({
    ...ASSIST_MIGRATION_DEFAULT_HIDDEN_COLUMNS,
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { data, isFetching, refetch } = useAssistMigrationProfiles({
    search: search || undefined,
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    ...primaryFilters,
    ...appliedFilters,
  });

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [search, primaryFilters, appliedFilters]);

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    setAppliedFilters(toAssistMigrationAppliedFilters(model));
  };

  const handlePrimaryFilterChange = (field: string, value: string) => {
    setPrimaryFilters((currentFilters) => {
      if (!value) {
        const nextFilters = { ...currentFilters };
        delete nextFilters[field];
        return nextFilters;
      }

      return { ...currentFilters, [field]: value };
    });
  };

  const profiles = useMemo(() => data?.profiles ?? [], [data]);
  const rows = useMemo(
    () => profiles.map((profile) => ({ ...profile, id: profile.businessId })),
    [profiles],
  );

  const businessSettingsKeys = useMemo(() => {
    const collected = collectKeys(
      profiles,
      (profile) => profile.businessSettings,
      HIDDEN_BUSINESS_SETTINGS_KEYS,
    );

    return Array.from(
      new Set([...ASSIST_MIGRATION_DEFAULT_BUSINESS_SETTINGS, ...collected]),
    ).sort();
  }, [profiles]);
  const featureFlagKeys = useMemo(
    () =>
      Array.from(
        new Set([
          ...ASSIST_MIGRATION_DEFAULT_FLAG_KEYS,
          ...collectKeys(profiles, (profile) => profile.featureFlags),
        ]),
      ).sort(),
    [profiles],
  );
  const booleanBusinessSettingsKeys = useMemo(
    () =>
      new Set([
        ...ASSIST_MIGRATION_DEFAULT_BUSINESS_SETTINGS,
        ...businessSettingsKeys.filter((key) =>
          profiles.some((profile) => typeof profile.businessSettings?.[key] === 'boolean'),
        ),
      ]),
    [businessSettingsKeys, profiles],
  );
  const featureFlagNameByKey = useMemo(
    () => new Map((data?.featureFlagCatalog ?? []).map((flag) => [flag.key, flag.name])),
    [data],
  );

  useEffect(() => {
    setVisibilityModel((currentVisibilityModel) =>
      getAssistMigrationColumnVisibilityModel({
        currentVisibilityModel,
        businessSettingsKeys,
        featureFlagKeys,
      }),
    );
  }, [businessSettingsKeys, featureFlagKeys]);

  const openMigration = (profile: AssistMigrationProfile) =>
    setModal({ open: true, businessId: profile.businessId, businessName: profile.businessName });

  const columns: GridColDef[] = useMemo(() => {
    const fixedColumns: GridColDef[] = [
      {
        headerName: 'Business ID',
        field: 'businessId',
        flex: 0.7,
        minWidth: 96,
        filterable: false,
        renderCell: (params: GridRenderCellParams<AssistMigrationProfile>) => (
          <Link
            to={`/businesses/${params.row.businessId}`}
            onClick={(event) => event.stopPropagation()}
          >
            {params.row.businessId}
          </Link>
        ),
      },
      {
        headerName: 'Business',
        field: 'businessName',
        flex: 2,
        minWidth: 96,
        filterable: false,
        renderCell: (params: GridRenderCellParams<AssistMigrationProfile>) => (
          <Link
            to={`/businesses/${params.row.businessId}`}
            onClick={(event) => event.stopPropagation()}
          >
            {params.row.businessName}
          </Link>
        ),
      },
      {
        headerName: 'Marketing #',
        field: 'marketingPhoneNumber',
        flex: 1,
        minWidth: 96,
        filterable: false,
      },
      {
        headerName: 'Transactional #',
        field: 'transactionalPhoneNumber',
        flex: 1,
        minWidth: 96,
        filterable: false,
      },
      {
        headerName: 'Laundroworks Connected',
        field: 'isLaundroworksConnected',
        type: 'boolean',
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<AssistMigrationProfile>) =>
          renderBoolean(params.row.isLaundroworksConnected),
      },
      {
        headerName: 'Laundroworks Customers',
        field: 'laundroworksCustomerCount',
        type: 'number',
        flex: 1,
        minWidth: 160,
        filterable: false,
      },
      {
        headerName: 'Action',
        field: 'action',
        flex: 1.5,
        minWidth: 96,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<AssistMigrationProfile>) => {
          const profile = params.row;

          if (profile.isMigratable) {
            return (
              <Button size="small" variant="contained" onClick={() => openMigration(profile)}>
                Migrate to Assist 2.0
              </Button>
            );
          }

          if (profile.version === AssistVersion.SELF_SERVE) {
            return <Chip size="small" color="success" label="Assist 2.0" />;
          }

          return (
            <Chip
              size="small"
              color={VERSION_CHIP_COLOR[profile.version]}
              label={ASSIST_VERSION_LABELS[profile.version]}
            />
          );
        },
      },
    ];

    const sortedBusinessSettingsKeys = [...businessSettingsKeys].sort((a, b) =>
      (BUSINESS_SETTING_LABELS[a] ?? a).localeCompare(BUSINESS_SETTING_LABELS[b] ?? b),
    );
    const sortedFeatureFlagKeys = [...featureFlagKeys].sort((a, b) =>
      (featureFlagNameByKey.get(a) ?? a).localeCompare(featureFlagNameByKey.get(b) ?? b),
    );

    const businessSettingsColumns: GridColDef[] = sortedBusinessSettingsKeys.map((key) => ({
      headerName: `BS ${BUSINESS_SETTING_LABELS[key] ?? key}`,
      field: `bs_${key}`,
      type: booleanBusinessSettingsKeys.has(key) ? 'boolean' : 'string',
      flex: 1,
      minWidth: 96,
      filterable: key !== 'isAssistEnabled',
      valueGetter: (params) => params.row.businessSettings?.[key],
      renderCell: (params: GridRenderCellParams<AssistMigrationProfile>) =>
        renderSettingValue(params.row.businessSettings?.[key]),
    }));

    const featureFlagColumns: GridColDef[] = sortedFeatureFlagKeys.map((key) => ({
      headerName: `FF ${featureFlagNameByKey.get(key) ?? key}`,
      field: `ff_${key}`,
      type: 'boolean',
      flex: 1,
      minWidth: 96,
      filterable: false,
      valueGetter: (params) => Boolean(params.row.featureFlags?.[key]),
      renderCell: (params: GridRenderCellParams<AssistMigrationProfile>) =>
        renderBoolean(Boolean(params.row.featureFlags?.[key])),
    }));

    return [...fixedColumns, ...businessSettingsColumns, ...featureFlagColumns];
  }, [booleanBusinessSettingsKeys, businessSettingsKeys, featureFlagKeys, featureFlagNameByKey]);

  return (
    <div className="page-container">
      <div className="assist-header">
        <h1>Business Profiles</h1>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          label="Search business"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setSearch(searchInput.trim());
            }
          }}
        />
        <Button variant="outlined" onClick={() => setSearch(searchInput.trim())}>
          Search
        </Button>
        <TextField
          select
          size="small"
          label="Business Settings Assist"
          InputLabelProps={{ sx: { fontSize: 12 } }}
          value={primaryFilters[ASSIST_MIGRATION_ASSIST_ENABLED_FILTER_FIELD] ?? ''}
          onChange={(event) =>
            handlePrimaryFilterChange(
              ASSIST_MIGRATION_ASSIST_ENABLED_FILTER_FIELD,
              event.target.value,
            )
          }
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Feature Flag Self-Serve Assist"
          InputLabelProps={{ sx: { fontSize: 12 } }}
          value={primaryFilters[ASSIST_MIGRATION_SELF_SERVE_FILTER_FIELD] ?? ''}
          onChange={(event) =>
            handlePrimaryFilterChange(ASSIST_MIGRATION_SELF_SERVE_FILTER_FIELD, event.target.value)
          }
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      </div>
      <div className="data-grid-container">
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isFetching}
          density="compact"
          slots={{ toolbar: AssistMigrationTableToolbar }}
          columnVisibilityModel={visibilityModel}
          onColumnVisibilityModelChange={setVisibilityModel}
          paginationMode="server"
          rowCount={data?.totalCount ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
        />
      </div>
      <AssistMigrationModal
        businessId={modal.businessId}
        businessName={modal.businessName}
        open={modal.open}
        onClose={() => setModal(CLOSED_MODAL)}
        onMigrated={() => refetch()}
      />
    </div>
  );
};
